import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { applicationApi, studentApi } from '@/api';
import {
  Alert,
  Button,
  FileUploader,
  LoadingState,
  Modal,
  Select,
  Textarea,
  useToast,
} from '@/components/ui';
import { RESUME_ACCEPT, RESUME_HINT, RESUME_MAX_SIZE } from '@/features/resume/upload';
import { isPlanLimitError } from '@/features/studentBilling/plans';
import { UpgradeNotice } from '@/features/studentBilling/UpgradeNotice';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { cn } from '@/utils/cn';

const CV_SOURCES = [
  { key: 'existing', label: 'Use a CV on file' },
  { key: 'upload', label: 'Upload a new CV' },
];

/**
 * Apply to one internship.
 *
 * A CV is required, and the server refuses an application without one. The
 * upload happens here rather than on the CV page so the student does not lose
 * their cover letter on the way:
 *
 * - no CV on file: upload one, and it becomes the primary;
 * - CVs on file: send one of them, or upload a new one just for this role.
 *   That one is added to the list but does not replace the primary, which
 *   other applications still rely on.
 */
export function ApplyDialog({ internship, open, onClose }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const [coverLetter, setCoverLetter] = useState('');
  const [resumeId, setResumeId] = useState('');
  const [cvSource, setCvSource] = useState('existing');

  const { data: resumes, isPending: resumesPending } = useQuery({
    queryKey: ['student-resumes'],
    queryFn: studentApi.listResumes,
    enabled: open,
  });

  const hasResumes = (resumes ?? []).length > 0;
  const primary = (resumes ?? []).find((resume) => resume.is_primary);

  const upload = useMutation({
    mutationFn: (file) => studentApi.uploadResume(file, { makePrimary: !hasResumes }),
    onSuccess: (resume) => {
      toast.success('CV uploaded — it will be sent with this application');
      // Land back on the picker with the new CV chosen, so the student sees
      // exactly what is about to be sent.
      // A first CV is the primary, which the picker offers as its own option.
      setResumeId(resume.is_primary ? '' : resume.id);
      setCvSource('existing');
      // The same set the CV page refreshes: the list, and the two screens
      // that count the CV towards profile completion.
      void queryClient.invalidateQueries({ queryKey: ['student-resumes'] });
      void queryClient.invalidateQueries({ queryKey: ['student-dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['student-profile'] });
    },
    onError: (uploadError) => toast.error(errorMessage(uploadError, 'Upload failed')),
  });

  const apply = useMutation({
    mutationFn: (input) => applicationApi.apply(input),
    onSuccess: () => {
      toast.success('Application submitted');
      void queryClient.invalidateQueries({ queryKey: ['internships'] });
      void queryClient.invalidateQueries({ queryKey: ['student-applications'] });
      void queryClient.invalidateQueries({ queryKey: ['student-dashboard'] });
      // The server drops the bookmark on apply; For you shows the new status.
      void queryClient.invalidateQueries({ queryKey: ['saved-internships'] });
      void queryClient.invalidateQueries({ queryKey: ['recommendations'] });
      setCoverLetter('');
      setResumeId('');
      setCvSource('existing');
      onClose();
      // Straight to the new application at the top of the Applied tab.
      navigate(paths.student.applications);
    },
    onError: (applyError) => {
      // A used-up monthly allowance is shown in the dialog as a way forward,
      // not as a toast that reads like something broke.
      if (isPlanLimitError(applyError)) return;
      toast.error(errorMessage(applyError, 'Could not submit the application'));
    },
  });
  const limited = apply.isError && isPlanLimitError(apply.error);

  function close() {
    apply.reset();
    onClose();
  }

  const uploader = (
    <FileUploader
      accept={RESUME_ACCEPT}
      maxSizeBytes={RESUME_MAX_SIZE}
      isUploading={upload.isPending}
      hint={RESUME_HINT}
      onSelect={(file) => upload.mutate(file)}
    />
  );

  return (
    <Modal
      open={open}
      title={`Apply to ${internship.title}`}
      description={internship.company?.name}
      onClose={close}
    >
      <div className="space-y-4">
        {limited && (
          <UpgradeNotice title="You have used this month's applications">
            {apply.error.message}
          </UpgradeNotice>
        )}

        {resumesPending && <LoadingState label="Checking your CV…" />}

        {!resumesPending && !hasResumes && (
          <>
            <Alert tone="warning" title="Upload your CV to apply">
              Companies review your CV with every application. Add one here and it becomes your
              primary CV, used for this and future applications.
            </Alert>
            {uploader}
          </>
        )}

        {hasResumes && (
          <div className="space-y-3">
            <div
              className="bg-surface-muted ring-line inline-flex gap-0.5 rounded-lg p-0.5 ring-1 ring-inset"
              role="tablist"
              aria-label="CV to send"
            >
              {CV_SOURCES.map((source) => (
                <button
                  key={source.key}
                  type="button"
                  role="tab"
                  aria-selected={cvSource === source.key}
                  onClick={() => setCvSource(source.key)}
                  className={cn(
                    'inline-flex h-7 items-center rounded-md px-3 text-[13px] font-medium whitespace-nowrap transition',
                    cvSource === source.key
                      ? 'bg-brand-600 shadow-brand-600/20 text-white shadow-sm'
                      : 'text-ink-muted hover:bg-brand-50 hover:text-ink',
                  )}
                >
                  {source.label}
                </button>
              ))}
            </div>

            {cvSource === 'existing' ? (
              <Select
                label="CV"
                hint="The CV the company will receive with this application."
                value={resumeId}
                onChange={(event) => setResumeId(event.target.value)}
              >
                <option value="">
                  {primary ? `Primary CV — ${primary.file_name}` : 'Primary CV'}
                </option>
                {(resumes ?? [])
                  .filter((resume) => !resume.is_primary)
                  .map((resume) => (
                    <option key={resume.id} value={resume.id}>
                      {resume.file_name}
                    </option>
                  ))}
              </Select>
            ) : (
              <div className="space-y-2">
                {uploader}
                <p className="text-ink-muted text-xs">
                  Sent with this application and kept on your CV page. Your primary CV stays as it
                  is.
                </p>
              </div>
            )}
          </div>
        )}

        <Textarea
          label="Cover letter"
          rows={6}
          hint="Optional, but a short note about why you fit helps."
          value={coverLetter}
          onChange={(event) => setCoverLetter(event.target.value)}
        />

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button
            disabled={!hasResumes || cvSource === 'upload' || upload.isPending || limited}
            isLoading={apply.isPending}
            onClick={() =>
              apply.mutate({
                internship_id: internship.id,
                ...(resumeId ? { resume_id: resumeId } : {}),
                ...(coverLetter.trim() ? { cover_letter: coverLetter.trim() } : {}),
              })
            }
          >
            Submit application
          </Button>
        </div>
      </div>
    </Modal>
  );
}
